import React, { useEffect, useRef, useState } from "react";
import { ActionIcon, Alert, Autocomplete, Button, Container, Divider, Group, Image, Indicator, MultiSelect, NumberInput, Paper, ScrollArea, Select, SimpleGrid, Space, Stack, Text, TextInput, Textarea, ThemeIcon, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle, IconArrowBackUp, IconCheck, IconChevronLeft, IconFile, IconLock, IconPhoto, IconTrash, IconUpload, IconX } from "@tabler/icons-react";

import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../RoleTemplate";
import { useAccess } from "../../Auth/auth-functions";
import { useGetLocationsQuery } from "../../api/location.api";
import { useGetAssetsQuery, useGetMakesQuery, useGetModelsQuery, useGetAssetTypesQuery } from "../../api/asset.api";
import { useGetVendorsQuery } from "../../api/vendor.api";
import { AssetDto, MutableAssetDto } from "../../models/assets.models";
import classes from "./AssetDetailView.module.css";

interface AssetEditViewProps {
	asset?: AssetDto | null;
	onSave: (values: MutableAssetDto, files: File[]) => void;
	onClose: () => void;
	isSaving?: boolean;
	onChanged?: (changed: boolean) => void;
}

export const AssetEditView: React.FC<AssetEditViewProps> = ({
	asset,
	onSave,
	onClose,
	isSaving = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { data: locations } = useGetLocationsQuery();
	const { data: assets } = useGetAssetsQuery();
	const { data: vendors } = useGetVendorsQuery();
	const { data: makes } = useGetMakesQuery();
	const { data: models } = useGetModelsQuery();
	const { data: types } = useGetAssetTypesQuery();
	const isNew = !asset || asset.AssetId === "new";
	const canEditAsset = useAccess(AzureADRoles.Admin);

	const locationItems = locations?.map(location => ({ value: location.LocationId, label: location.Title })) || [];
	const assetItems = assets
		?.filter(a => a.AssetId !== asset?.AssetId && (a.ParentAsset == null || a.ParentAsset?.AssetId !== asset?.AssetId))
		.map(a => ({ value: a.AssetId, label: `${a.Name}${a.AssetTag ? ` (${a.AssetTag})` : ""}` })) || [];
	const vendorItems = (vendors || []).map(vendor => ({ value: vendor.VendorId, label: vendor.Name }));

	const [uploadImages, setUploadImages] = useState<FileWithPath[]>([]);
	const [uploadFiles, setUploadFiles] = useState<FileWithPath[]>([]);
	const [filesToDelete, setFilesToDelete] = useState<string[]>([]);

	const form = useForm<MutableAssetDto>({
		initialValues: {
			AssetId: asset?.AssetId || "",
			Name: asset?.Name || "",
			AssetTag: asset?.AssetTag || "",
			Description: asset?.Description || "",
			AssetType: asset?.AssetType || "",
			Location: asset?.Location?.LocationId || "",
			DateOfPurchase: asset?.DateOfPurchase ? new Date(asset.DateOfPurchase) : undefined,
			DateOfInstallation: asset?.DateOfInstallation ? new Date(asset.DateOfInstallation) : undefined,
			Make: asset?.Make || "",
			Model: asset?.Model || "",
			SerialNumber: asset?.SerialNumber || "",
			Vendors: asset?.Vendors?.map(vendor => vendor.VendorId) || [],
			ParentAsset: asset?.ParentAsset?.AssetId || null,
			FilesToDelete: []
		},
		validate: {
			Name: (value) => (value.length < 3 ? "Name must be at least 3 characters long" : null),
			Location: (value) => (!value ? "Location is required" : null)
		}
	});

	const isChanged = form.isDirty() || uploadImages.length > 0 || uploadFiles.length > 0 || filesToDelete.length > 0;

	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== isChanged && canEditAsset) {
			onChanged(isChanged);
			prevDirty.current = isChanged;
		}
	}, [isChanged, onChanged]);

	useEffect(() => {
		form.setValues({
			Name: asset?.Name || "",
			AssetTag: asset?.AssetTag || "",
			Description: asset?.Description || "",
			AssetType: asset?.AssetType || "",
			Location: asset?.Location?.LocationId || "",
			DateOfPurchase: asset?.DateOfPurchase ? new Date(asset.DateOfPurchase) : undefined,
			DateOfInstallation: asset?.DateOfInstallation ? new Date(asset.DateOfInstallation) : undefined,
			Make: asset?.Make || "",
			Model: asset?.Model || "",
			SerialNumber: asset?.SerialNumber || "",
			Vendors: asset?.Vendors?.map(vendor => vendor.VendorId) || [],
			ParentAsset: asset?.ParentAsset?.AssetId || null,
			FilesToDelete: []
		});
		setFilesToDelete([]);
		setUploadImages([]);
		setUploadFiles([]);
		// eslint-disable-next-line
	}, [asset]);

	useEffect(() => {
		form.setValues({
			...form.values,
			FilesToDelete: filesToDelete
		});
	}, [filesToDelete]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		const files = [...uploadImages, ...uploadFiles];
		onSave(values, files);
	};

	const handleRemoveUploadedFile = (fileId: string) => {
		setFilesToDelete((files) => [...files, fileId]);
	};

	const handleUndoRemoveUploadedFile = (fileId: string) => {
		setFilesToDelete((files) => files.filter(id => id !== fileId));
	};

	const handleRemoveNewFile = (index: number) => {
		setUploadFiles((files) => files.filter((_, i) => i !== index));
	};

	const handleRemoveNewImage = (index: number) => {
		setUploadImages((files) => files.filter((_, i) => i !== index));
	};

	if (asset?.Deleted != null) {
		return (
			<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
				<Stack gap="xs" mt="xl" justify="center" align="center">
					<ThemeIcon size={80} radius={80} color="red" variant="light">
						<IconTrash size={40} />
					</ThemeIcon>
					<Text fz="xl" fw="bold">
						Asset Deleted
					</Text>
					<Text ta="center">This asset has been deleted and cannot be edited.</Text>
				</Stack>
			</Paper>
		);
	}

	const noAccess = (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? "calc(100vh)" : "calc(100vh - 158px)" }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconLock size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">No Access</Text>
				<Text ta="center">You do not have permission to create or edit assets. Please contact your administrator.</Text>
			</Stack>
		</Paper>
	);

	return (
		<RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
			<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
				<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? "calc(100vh)" : "calc(100vh - 160px)" }}>
					<Stack gap={0} mb="md" justify="center" className={classes["header"]}>
						<Group justify="space-between" px="20" wrap="nowrap">
							<Group gap="xs" wrap="nowrap">
								<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
									Back
								</Button>
								<Title order={isMobile ? 6 : 3}>{isNew ? "New Asset" : "Edit Asset"}</Title>
							</Group>
							<Button
								onClick={() => handleSave(form.values)}
								disabled={!isChanged}
								loading={isSaving}
								variant="filled"
								radius="xl"
								miw={100}
								leftSection={<IconCheck />}
							>
								Save
							</Button>
						</Group>
					</Stack>
					<Container px="20">
						{form.errors && Object.keys(form.errors).length > 0 && (
							<>
								<Alert title="Please fix the following errors:" color="red" mb="md" radius="lg">
									<Stack gap={0} mb={0}>
										{Object.entries(form.errors).map(([field, error]) => error && (
											<Group gap={5} key={field}>
												<ThemeIcon c="red" w={20} variant="transparent" m={-5}><IconAlertTriangle size={14} /></ThemeIcon>
												<Text key={field} fz="sm">{error}</Text>
											</Group>
										))}
									</Stack>
								</Alert>
								<Divider mb="md" />
							</>
						)}
						<form onSubmit={form.onSubmit((values) => handleSave(values))}>
							<Button type="submit" style={{ display: "none" }} />
							<TextInput
								label="Name"
								key={form.key("Name")}
								{...form.getInputProps("Name")}
								placeholder="Enter asset name"
								maxLength={255}
								required
								mb="xl"
							/>

							<Textarea
								label="Description"
								key={form.key("Description")}
								{...form.getInputProps("Description")}
								placeholder="Enter asset description"
								mb="xl"
								autosize
								minRows={3}
							/>

							<SimpleGrid cols={isMobile ? 1 : 2} spacing="xl" mb="xl">
								<TextInput
									label="Asset Tag ID"
									key={form.key("AssetTag")}
									{...form.getInputProps("AssetTag")}
									placeholder="Enter asset tag ID"
									maxLength={255}
								/>

								<Autocomplete
									label="Asset Type"
									key={form.key("AssetType")}
									{...form.getInputProps("AssetType")}
									data={types || []}
									placeholder="Enter or select asset type"
								/>

								<DatePickerInput
									label="Date of Purchase"
									key={form.key("DateOfPurchase")}
									value={form.values.DateOfPurchase ?? null}
									onChange={(value) => form.setFieldValue("DateOfPurchase", value)}
									placeholder="Select purchase date"
									clearable
								/>
								
								<DatePickerInput
									label="Date of Installation"
									key={form.key("DateOfInstallation")}
									value={form.values.DateOfInstallation ?? null}
									onChange={(value) => form.setFieldValue("DateOfInstallation", value)}
									placeholder="Select installation date"
									clearable
								/>

								<Autocomplete
									label="Make"
									key={form.key("Make")}
									{...form.getInputProps("Make")}
									data={makes || []}
									placeholder="Enter or select make"
								/>
								<Autocomplete
									label="Model"
									key={form.key("Model")}
									{...form.getInputProps("Model")}
									data={models || []}
									placeholder="Enter or select model"
								/>
								<TextInput
									label="Serial Number"
									key={form.key("SerialNumber")}
									{...form.getInputProps("SerialNumber")}
									placeholder="Enter serial number"
								/>
							</SimpleGrid>

							<MultiSelect
								label="Vendors"
								key={form.key("Vendors")}
								data={vendorItems}
								{...form.getInputProps("Vendors")}
								placeholder="Select vendors"
								checkIconPosition="right"
								searchable
								mb="xl"
							/>
							
							<Select
								label="Location"
								key={form.key("Location")}
								{...form.getInputProps("Location")}
								data={locationItems}
								placeholder="Select location"
								checkIconPosition="left"
								required
								searchable
								mb="xl"
							/>

							<Select
								label="Parent Asset"
								key={form.key("ParentAsset")}
								{...form.getInputProps("ParentAsset")}
								data={assetItems}
								placeholder="Select parent asset"
								checkIconPosition="left"
								searchable
								clearable
								mb="xl"
							/>

							<Stack gap="xs" mb="xl">
								<Text fz="sm" fw="bold">Pictures</Text>
								<SimpleGrid cols={(asset?.Files?.filter(file => file.IsImage).length ?? 0) + uploadImages.length > 0 ? 3 : 1} spacing="xs">
									{asset?.Files?.filter(file => file.IsImage).map((file) => (
										<React.Fragment key={file.FileId}>
											{filesToDelete.includes(file.FileId) ? (
												<Indicator
													label={
														<Tooltip label="Undo Remove">
															<ActionIcon onClick={() => handleUndoRemoveUploadedFile(file.FileId)} color="dimmed" radius="xl" size="sm">
																<IconArrowBackUp />
															</ActionIcon>
														</Tooltip>
													}
													size={16}
													color="transparent"
													offset={15}
													position="top-end"
												>
													<Image src={file.Url} alt={file.Name} radius="md" style={{ objectFit: "cover", aspectRatio: "4/3", width: "100%", height: "auto", filter: "opacity(0.35)" }} />
												</Indicator>
											) : (
												<Indicator
													label={
														<Tooltip label="Remove">
															<ActionIcon onClick={() => handleRemoveUploadedFile(file.FileId)} color="red" radius="xl" size="sm">
																<IconX />
															</ActionIcon>
														</Tooltip>
													}
													size={16}
													color="transparent"
													offset={15}
													position="top-end"
												>
													<Image src={file.Url} alt={file.Name} radius="md" style={{ objectFit: "cover", aspectRatio: "4/3", width: "100%", height: "auto" }} />
												</Indicator>
											)}
										</React.Fragment>
									))}
									{uploadImages.map((file, index) => (
										<Indicator
											key={index}
											label={
												<Tooltip label="Remove">
													<ActionIcon onClick={(e) => { e.preventDefault(); handleRemoveNewImage(index); }} color="red" radius="xl" size="sm">
														<IconX />
													</ActionIcon>
												</Tooltip>
											}
											size={16}
											color="transparent"
											offset={15}
											position="top-end"
										>
											<Image src={URL.createObjectURL(file)} alt={file.name} radius="md" style={{ objectFit: "cover", aspectRatio: "4/3", width: "100%", height: "auto" }} />
										</Indicator>
									))}
									<Dropzone
										onDrop={(files) => setUploadImages((prev) => [...prev, ...files])}
										accept={['image/png', 'image/jpeg']}
										radius="md"
										mih={120}
									>
										<Stack justify="center" align="center" gap="md" style={{ pointerEvents: "none" }}>
											<Dropzone.Accept>
												<IconUpload size={25} color="var(--mantine-color-blue-6)" stroke={1.5} />
											</Dropzone.Accept>
											<Dropzone.Reject>
												<IconX size={25} color="var(--mantine-color-red-6)" stroke={1.5} />
											</Dropzone.Reject>
											<Dropzone.Idle>
												<IconPhoto size={25} color="var(--mantine-color-dimmed)" stroke={1.5} />
											</Dropzone.Idle>
											<Text size="sm" inline style={{ textAlign: "center" }}>
												Drag images here or click to select files
											</Text>
										</Stack>
									</Dropzone>
								</SimpleGrid>
							</Stack>

							<Stack gap="xs" mb="xl">
								<Text fz="sm" fw="bold">Files</Text>
								<Stack gap="xs">
									{asset?.Files?.filter(file => !file.IsImage).map((file) => (
										<Group key={file.FileId} gap="xs" align="center">
											<ThemeIcon size="sm" radius="xl" variant="light"><IconFile size={14} /></ThemeIcon>
											<Text fz="sm">{file.Name}</Text>
											{filesToDelete.includes(file.FileId) ? (
												<Tooltip label="Undo Remove">
													<ActionIcon onClick={() => handleUndoRemoveUploadedFile(file.FileId)} color="dimmed" radius="xl" size="sm">
														<IconArrowBackUp />
													</ActionIcon>
												</Tooltip>
											) : (
												<Tooltip label="Remove">
													<ActionIcon onClick={() => handleRemoveUploadedFile(file.FileId)} color="red" radius="xl" size="sm">
														<IconX />
													</ActionIcon>
												</Tooltip>
											)}
										</Group>
									))}
									{uploadFiles.map((file, index) => (
										<Group key={index} gap="xs" align="center">
											<ThemeIcon size="sm" radius="xl" variant="light"><IconFile size={14} /></ThemeIcon>
											<Text fz="sm">{file.name}</Text>
											<Tooltip label="Remove">
												<ActionIcon onClick={(e) => { e.preventDefault(); handleRemoveNewFile(index); }} color="red" radius="xl" size="sm">
													<IconX />
												</ActionIcon>
											</Tooltip>
										</Group>
									))}
									<Dropzone
										onDrop={(files) => setUploadFiles((prev) => [...prev, ...files])}
										radius="md"
										mih={80}
									>
										<Stack justify="center" align="center" gap="sm" style={{ pointerEvents: "none" }}>
											<Dropzone.Accept>
												<IconUpload size={20} color="var(--mantine-color-blue-6)" stroke={1.5} />
											</Dropzone.Accept>
											<Dropzone.Reject>
												<IconX size={20} color="var(--mantine-color-red-6)" stroke={1.5} />
											</Dropzone.Reject>
											<Dropzone.Idle>
												<IconFile size={20} color="var(--mantine-color-dimmed)" stroke={1.5} />
											</Dropzone.Idle>
											<Text size="sm" inline style={{ textAlign: "center" }}>
												Drag files here or click to select
											</Text>
										</Stack>
									</Dropzone>
								</Stack>
							</Stack>
						</form>
					</Container>
				</ScrollArea>
			</Paper>
		</RoleTemplate>
	);
};

export default AssetEditView;
